import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { isoForLang } from "../lib/langCodes";
import { Trash2, Save } from "lucide-react";

import ConversationLanguageBar from "./conversation/ConversationLanguageBar";
import HandsFreeToggle from "./conversation/HandsFreeToggle";
import ConversationMicCards from "./conversation/ConversationMicCards";
import ConversationTranscript from "./conversation/ConversationTranscript";
import { useTTSPlayback } from "../hooks/useTTSPlayback";
import { useVADHandsFree } from "../hooks/useVADHandsFree";

/**
 * Two-way conversation: each side has a mic. STT → translate to the OTHER
 * side → speak. The heavy lifting lives in:
 *   - useTTSPlayback     audio playback + fallback
 *   - useVADHandsFree    hands-free VAD loop + state
 *   - ConversationLanguageBar / MicCards / Transcript / HandsFreeToggle  UI bits
 *
 * This component is the glue: it owns the language state + turns list, and
 * exposes `processTurn` which the manual mic and the VAD loop both call.
 */
export default function ConversationMode({ user, voice = "nova", onSaveSession }) {
  const [therapistLang, setTherapistLang] = useState("English");
  const [patientLang, setPatientLang] = useState("Spanish");
  const [turns, setTurns] = useState([]);
  const [recording, setRecording] = useState(null); // 'therapist' | 'patient' | null
  const [working, setWorking] = useState(null);     // 'therapist' | 'patient' | null

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const transcriptRef = useRef(null);
  const lastSpeakerRef = useRef("therapist");

  const { speak, stop: stopAudio, audioRef } = useTTSPlayback(voice);

  // Auto-scroll transcript to latest
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [turns]);

  const processTurn = async (speaker, blob, opts = {}) => {
    const { autoRoute = false } = opts;
    setWorking(speaker);
    let turnId = null;
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `recording.${ext}`);
      if (!autoRoute) {
        const fromLangHint = speaker === "therapist" ? therapistLang : patientLang;
        fd.append("language", fromLangHint);
      }
      const t = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const original = (t.data?.text || "").trim();
      const detected = (t.data?.detected_language || "").toLowerCase();
      if (!original) {
        if (!autoRoute) toast.error("Couldn't catch that — please try again");
        return;
      }

      // Decide which side just spoke (manual = whoever tapped; auto-route =
      // pick by detected language with a fallback to alternating).
      let resolvedSpeaker = speaker;
      let fromLang, toLang;
      if (autoRoute) {
        const tIso = isoForLang(therapistLang).toLowerCase();
        const pIso = isoForLang(patientLang).toLowerCase();
        if (detected && tIso && detected.startsWith(tIso)) {
          resolvedSpeaker = "therapist";
        } else if (detected && pIso && detected.startsWith(pIso)) {
          resolvedSpeaker = "patient";
        } else {
          resolvedSpeaker = lastSpeakerRef.current === "therapist" ? "patient" : "therapist";
        }
        fromLang = resolvedSpeaker === "therapist" ? therapistLang : patientLang;
        toLang = resolvedSpeaker === "therapist" ? patientLang : therapistLang;
      } else {
        fromLang = speaker === "therapist" ? therapistLang : patientLang;
        toLang = speaker === "therapist" ? patientLang : therapistLang;
      }
      lastSpeakerRef.current = resolvedSpeaker;

      // 1) Show transcribed text immediately (huge perceived-latency win).
      turnId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const pendingTurn = {
        id: turnId,
        speaker: resolvedSpeaker,
        original,
        translation: "",
        translating: true,
        fromLang,
        toLang,
        at: new Date().toISOString(),
      };
      setTurns((p) => [...p, pendingTurn]);

      // 2) Combined translate + speak — one round trip.
      const res = await api.post(
        "/translate_speak",
        {
          text: original,
          source_lang: fromLang,
          target_lang: toLang,
          discipline: user.discipline,
          voice,
          speed: 0.95,
        },
        { responseType: "blob" }
      );
      let translation = "";
      try {
        translation = decodeURIComponent(res.headers["x-translation"] || "");
      } catch {
        translation = "";
      }
      setTurns((p) =>
        p.map((tn) => (tn.id === turnId ? { ...tn, translation, translating: false } : tn))
      );

      // Play the audio that's already in our hand.
      try {
        const url = URL.createObjectURL(res.data);
        stopAudio();
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => URL.revokeObjectURL(url);
        await audio.play();
      } catch {
        speak(translation, toLang);
      }
    } catch (err) {
      if (turnId) {
        setTurns((p) => p.filter((tn) => tn.id !== turnId));
      }
      if (!autoRoute) toast.error(err?.response?.data?.detail || "Could not process turn");
    } finally {
      setWorking(null);
    }
  };

  // Manual recording (per-side mic button)
  const stopRecording = () => {
    try {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    } catch { /* ignore */ }
  };

  const startRecording = async (speaker) => {
    if (handsFree) {
      toast.error("Turn off hands-free mode first");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Microphone not supported on this device");
      return;
    }
    if (recording) {
      stopRecording();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeOptions = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mimeType = mimeOptions.find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data?.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        setRecording(null);
        if (blob.size === 0) {
          toast.error("No audio captured");
          return;
        }
        await processTurn(speaker, blob);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(speaker);
    } catch {
      toast.error("Microphone permission denied");
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
    }
  };

  // Hands-free / VAD
  const { handsFree, hfStatus, hfLevel, toggle: toggleHandsFree, stop: stopHandsFree } =
    useVADHandsFree({
      onUtterance: (blob) => processTurn("therapist", blob, { autoRoute: true }),
      disabled: !!recording || !!working,
    });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopHandsFree();
      try {
        if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      } catch { /* ignore */ }
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      stopAudio();
    };
     
  }, []);

  const swap = () => {
    setTherapistLang(patientLang);
    setPatientLang(therapistLang);
  };

  const clearTranscript = () => {
    setTurns([]);
    toast.success("Transcript cleared");
  };

  const saveAsSession = () => {
    if (turns.length === 0) return;
    const lines = turns.map((t) =>
      `[${new Date(t.at).toLocaleTimeString()}] ${t.speaker === "therapist" ? user.full_name : "Patient"} (${t.fromLang} → ${t.toLang})\n` +
      `  Said: ${t.original}\n` +
      `  Translation: ${t.translation}`
    );
    const notes = `Conversation transcript:\n\n${lines.join("\n\n")}`;
    onSaveSession?.(notes);
  };

  return (
    <div className="space-y-6" data-testid="conversation-mode-pane">
      <ConversationLanguageBar
        therapistLang={therapistLang}
        patientLang={patientLang}
        onTherapistChange={setTherapistLang}
        onPatientChange={setPatientLang}
        onSwap={swap}
      />

      <HandsFreeToggle
        handsFree={handsFree}
        hfStatus={hfStatus}
        hfLevel={hfLevel}
        onToggle={toggleHandsFree}
        disabled={!!recording || !!working}
      />

      <ConversationMicCards
        therapistLang={therapistLang}
        patientLang={patientLang}
        recording={recording}
        working={working}
        onTherapistClick={() => (recording === "therapist" ? stopRecording() : startRecording("therapist"))}
        onPatientClick={() => (recording === "patient" ? stopRecording() : startRecording("patient"))}
      />

      <div className="border border-zinc-200 rounded-xl bg-white">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">Conversation transcript</p>
          <div className="flex gap-2">
            {turns.length > 0 && onSaveSession && (
              <Button variant="outline" size="sm" onClick={saveAsSession} data-testid="conv-save-session">
                <Save className="w-4 h-4 mr-1" /> Save to session log
              </Button>
            )}
            {turns.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearTranscript} data-testid="conv-clear">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <ConversationTranscript
          turns={turns}
          transcriptRef={transcriptRef}
          onReplay={(t) => speak(t.translation, t.toLang)}
        />
      </div>
    </div>
  );
}
